import { useEffect } from 'react';
import { ButtonColors } from 'types';
import { useAppSelector } from 'store';
import { selectAuthUser } from 'store/slices/auth';
import { calculateProcessingFees } from 'utils/helpers';
import { AppRoutes } from 'config';
import { useAlert, useNavigate, useSelectedCloverDevice } from 'hooks';
import {
  Icon,
  Flex,
  Button,
  TerminalApprove,
  TerminalDecline,
  TerminalCompletePurchase,
} from 'components';

import { useRemainderCart } from '../hooks';
import { TemplateDialog } from '../template-dialog';

import { CreditProps } from './types';
import { RecoveryAttention } from './recovery-attention';
import { useCreditPayment } from './use-credit-payment';

export const Credit = ({
  event,
  amount,
  partialBalance,
  checkoutId,
  isProcessingFeeEnabled,
  onAddCreditPaymentMethod,
  onPaymentProtectionChange,
}: CreditProps) => {
  const { showErrorAlert, showSuccessAlert } = useAlert();
  const navigate = useNavigate();
  const authUser = useAppSelector(selectAuthUser);
  const { remainder, isBalanceZero } = useRemainderCart(
    amount,
    isProcessingFeeEnabled
  );

  const { selectedDeviceId: deviceId } = useSelectedCloverDevice(event);
  const processingFeeAmount = calculateProcessingFees(
    partialBalance ? partialBalance.requiredRemaining / 100 : remainder,
    isProcessingFeeEnabled ? Number(authUser?.company?.processingFee) : 0
  );

  const {
    paymentStatus,
    isCheckingExistingPayment,
    isTakingLonger,
    canCheckPaymentStatus,
    onCheckPaymentStatus,
    isCheckingStatus,
    approvalConfirmed,
    isSavingSignature,
    recoveryNotice,
    onReturnToCheckout,
    terminalUnreachable,
    signaturePending,
    onCancelPayment,
    isCancelDisabled,
    showPaymentDialog,
    handleCreditPayment,
    paymentPendingAnimation,
    handleAnimationComplete,
  } = useCreditPayment({
    checkoutId,
    deviceId: deviceId!,
    creditAmounts: partialBalance
      ? partialBalance.requiredRemaining / 100
      : remainder,
    tipAmount: partialBalance ? partialBalance.tipRemaining : undefined,
    eventId: Number(event?.id),
    processingFee: processingFeeAmount,
    onSuccessPayment: onAddCreditPaymentMethod,
    onSaleParked: () => {
      showSuccessAlert('Payment approved and saved', {
        description: 'The terminal lost its connection, so this sale moved to Receipts (Pending sale). You can continue selling.',
      });
      navigate(AppRoutes.Products.Home.replace(':id', String(event?.id)));
    },
  });

  // The approved state replaces the card animation; never leave a result
  // waiting on an animation that is no longer shown.
  useEffect(() => {
    if (approvalConfirmed && signaturePending && !isSavingSignature) handleAnimationComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalConfirmed, signaturePending, isSavingSignature]);

  const protectedPayment = showPaymentDialog || !!recoveryNotice;
  useEffect(() => {
    onPaymentProtectionChange?.(protectedPayment);
  }, [protectedPayment, onPaymentProtectionChange]);

  const onOpen = () => {
    if (!deviceId) {
      return showErrorAlert(
        'No device added for event. Contact the company owner to add a device'
      );
    }

    if (partialBalance ? partialBalance.remaining <= 0 : isBalanceZero) {
      return showErrorAlert(
        'The balance is zero, it is unable to make a payment by card'
      );
    }

    handleCreditPayment();
  };

  return (
    <>
      {recoveryNotice && !showPaymentDialog && (
        <RecoveryAttention
          key={`${recoveryNotice.title}:${recoveryNotice.description}`}
          notice={recoveryNotice}
          onContinue={onOpen}
        />
      )}
      <TemplateDialog
        onOpen={onOpen}
        triggerTitle="Credit"
        icon={<Icon.Credit />}
        open={showPaymentDialog}
        closeOnDocumentClick={false}
        closeOnEscape={false}
        showCloseButton={false}
        title={
          isSavingSignature
            ? 'Payment approved — saving signature'
            : approvalConfirmed && signaturePending
            ? 'Payment approved — signature pending'
            : isCheckingExistingPayment
            ? 'Checking payment status'
            : isTakingLonger
              ? 'Still waiting for payment confirmation'
              : 'Complete purchase on terminal'
        }
        onClose={onReturnToCheckout}
      >
        {(close) => {
          if (paymentStatus === 'idle') {
            return (
              <Flex column gap={16} fullWidth alignItems="center">
                {approvalConfirmed && signaturePending && !isSavingSignature ? (
                  // The card is already approved: no card-insertion animation.
                  <p aria-live="polite" style={{ padding: '32px 16px', textAlign: 'center', lineHeight: 1.6 }}>
                    Card approved. Waiting for the customer to sign on Clover. <strong>DO NOT PRESENT THE CARD AGAIN.</strong>
                  </p>
                ) : isCheckingExistingPayment ? (
                  <p aria-live="polite" style={{ padding: '32px 16px', textAlign: 'center', lineHeight: 1.6 }}>
                    Checking the existing payment. <strong>DO NOT PRESENT THE CARD AGAIN.</strong>
                  </p>
                ) : (
                  <TerminalCompletePurchase onComplete={handleAnimationComplete} />
                )}
                {isTakingLonger && (
                  <>
                    <div
                      role="status"
                      aria-live="polite"
                      style={{
                        width: '100%',
                        maxWidth: 420,
                        padding: '16px 20px',
                        boxSizing: 'border-box',
                        borderRadius: 12,
                        background: '#f2f6ff',
                        lineHeight: 1.6,
                        textAlign: 'left',
                      }}
                    >
                      <p style={{ margin: '0 0 8px', fontWeight: 600 }}>
                        {isSavingSignature
                          ? 'Signature received. Saving…'
                          : approvalConfirmed && signaturePending
                          ? 'Finish signing on Clover.'
                          : 'Waiting for Clover to confirm the payment.'}
                      </p>
                      <p style={{ margin: 0 }}>
                        {isSavingSignature
                          ? 'Keep this checkout open. The customer does not need to sign again.'
                          : approvalConfirmed && signaturePending
                          ? canCheckPaymentStatus
                            ? 'You can check the payment status without interrupting the signature.'
                            : 'Keep this checkout open while the customer signs.'
                          : canCheckPaymentStatus
                            ? 'Keep this checkout open. Use Check payment status below to check the existing payment.'
                            : 'Keep this checkout open while Clover confirms the payment.'}
                      </p>
                      {terminalUnreachable && !approvalConfirmed && (
                        <p style={{ margin: '12px 0 0' }}>
                          The terminal lost its connection. Make sure Clover is online and Cloud Pay Display is open. This sale stays protected.
                        </p>
                      )}
                      {!(approvalConfirmed && signaturePending) && (
                        <p style={{ margin: '12px 0 0', paddingTop: 12, borderTop: '1px solid #d9e1ef', fontWeight: 800 }}>
                          DO NOT PROCESS THE CARD AGAIN OR RESET THE CART.
                        </p>
                      )}
                    </div>
                    {canCheckPaymentStatus && (
                      <Button
                        fullWidth
                        text={
                          isCheckingStatus
                            ? 'Checking payment status…'
                            : 'Check payment status'
                        }
                        disabled={isCheckingStatus}
                        onClick={onCheckPaymentStatus}
                      />
                    )}
                  </>
                )}
                <Button
                  fullWidth
                  text="Cancel payment"
                  onClick={onCancelPayment}
                  colorType={ButtonColors.LightRed}
                  disabled={isCancelDisabled || paymentPendingAnimation}
                />
              </Flex>
            );
          }

          if (paymentStatus === 'failure') {
            return <TerminalDecline onComplete={close} />;
          }

          if (paymentStatus === 'success') {
            return <TerminalApprove onComplete={close} />;
          }
        }}
      </TemplateDialog>
    </>
  );
};
